import React, { useRef, useEffect, useState } from "react";
import { SvgViewProps } from "../types/view";
import WebFont from "webfontloader";
import { FontFamily, TextNode } from "../types";
import { unsanitizeSVG } from "../utils/common-utils";

/**
 * Load SVG image
 * @param props
 * @returns
 */
export default function SvgView(props: Readonly<SvgViewProps>) {
  const loadedFontFamilies = useRef<Array<FontFamily>>([]);
  const objectRef = useRef<HTMLObjectElement>(null); 
  const [textColors, setTextColors] = useState<{ [key: string]: string }>({});


  useEffect(() => {
    if (!props.imageUrl) return;
    fetch(props.imageUrl)
      .then((res) => res.text())
      .then((svg) => {
        renderFigmaDesign (svg);
        props.setIsImageLoad(true);
      })
      .catch((err) => {
        console.error("Error loading SVG:", err);
      });
  }, [props.imageUrl]);

  /**
   * Update the design image by hiding the updated text element and changing text color
   * @param node 
   */
  const updateDesignImage = (node: TextNode) => {
    const objectElement = document.querySelector('#design-object') as HTMLObjectElement | null;
    let svgElement: SVGSVGElement | null = null;
    if (objectElement && objectElement.contentDocument) {
      svgElement = objectElement.contentDocument.querySelector('svg');
      const textElement = svgElement?.querySelector(`text[id="${node.id}"]`);
      if (textElement) {
        textColors[node.id] = textElement.getAttribute("fill") || "black";
        (textElement as HTMLElement).style.display = "none";
      }
    }
  };

  /**
   * Render the SVG design as an object element
   * @param svg 
   */
  const renderFigmaDesign  = (svg: string) => {
    svg = unsanitizeSVG(svg);
    const blob = new Blob([svg], { type: "image/svg+xml" });
     if (objectRef.current) {
      objectRef.current.data = URL.createObjectURL(blob);
      objectRef.current.onload = () => {
        props.setIsImageLoad(true);
        const svgDoc = objectRef.current?.contentDocument;
        if (svgDoc) {
          const svgEl = svgDoc.querySelector("svg");
          if (svgEl) {
            // Resize SVG
            svgEl.setAttribute("width", `${props.data.frame_width}`);
            svgEl.setAttribute("height", `${props.data.frame_height}`);
            svgEl.setAttribute("viewBox", `0 0 ${props.data.frame_width} ${props.data.frame_height}`);
            svgEl.setAttribute("preserveAspectRatio", "xMidYMid meet");

            // Load fonts inside the design
            const styleEl = svgDoc.createElementNS("http://www.w3.org/2000/svg", "style");
            styleEl.textContent = `
              @import url('https://fonts.googleapis.com/css2?family=${loadedFontFamilies.current[0].family}:wght@400;700&display=swap');
              text { font-family: '${loadedFontFamilies.current[0].family}', sans-serif; }
            `;
            svgEl.insertBefore(styleEl, svgEl.firstChild);

            // Get text colors frm SVG
            const newColors: { [key: string]: string } = {};
            props.data.text_nodes.forEach((node) => {
              if (node.is_updated || props.updatedTextNodes.find(n => n.id === node.id)) {
                const textElement = svgEl.querySelector(`text[id="${node.id}"]`);
                if (textElement) {
                  newColors[node.id] = textElement.getAttribute("fill") || "black";
                  (textElement as HTMLElement).style.display = "none";
                }
              }
            });
            setTextColors(newColors);
          }
        }
      }
    }
  };

  const loadFont = (fontFamily: FontFamily) => {
    // trying google fonts as it is freely available
    // check if this font family is already loaded
    // if so skip
    if (
      loadedFontFamilies.current.find(
        (loadedFontFamily) =>
          loadedFontFamily.family === fontFamily.family &&
          loadedFontFamily.style === fontFamily.style &&
          loadedFontFamily.weight === fontFamily.weight
      )
    ) {
      return;
    }

    // add this font to loaded fonts var
    loadedFontFamilies.current.push(fontFamily);

    // construct the font family string as per Google Fonts API - https://developers.google.com/fonts/docs/getting_started#Syntax
    // load fonts with both style string  & numeric weight
    WebFont.load({
      google: {
        families: [
          // TODO: Enable this after debugging: `${fontFamily.family}:${fontFamily.style.toLowerCase()}`,
          `${fontFamily.family}:${fontFamily.weight}`,
        ],
      },
    });
  };

  /**
   * Calculate proportionally scaled down font-size for a textnode
   * based on its max char count
   * @param textNode
   */
  const calcFontSize = (textNode: TextNode) => {
    return textNode.characters.length >= textNode.characterWarningLength / 2
      ? textNode.fontSize / 2
      : textNode.fontSize;
  };

  const getTextAlignment = (horizontalAlignment: string) => {
    if (!horizontalAlignment) return 'left';

    switch (horizontalAlignment) {
      case "LEFT": return 'left';
      case "CENTER": return 'center';
      case "RIGHT": return 'right';
      case "JUSTIFIED": return 'left';
      default: return 'left';
    }
  };

  return (
    <>
      <object
        ref={objectRef}
        id="design-object"
        type="image/svg+xml"
      />
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`0 0 ${props.data.frame_width} ${props.data.frame_height}`}
        width={props.data.frame_width}
        height={props.data.frame_height}
        style={{ width: "100%", height: "100%" }}
      >
        {/* Use a group (<g>) instead of a rect as the container */}
        <g style={{ isolation: "isolate" }}>
          {props.data.image_nodes.map((node) => {
            const updatedNode = props.updatedImageNodes.find(n => n.id === node.id);

            return (
              <React.Fragment key={node.id}>
                <rect
                  id={node.id}
                  data-text={node.id}
                  x={node.x}
                  y={node.y}
                  width={node.width}
                  height={node.height}
                  className="figma-press-image-node"
                  pointerEvents="all"
                  onClick={() => props.handleImageClick(node)}
                />
                {props.isImageLoad && (node.is_updated || updatedNode) && (
                  <foreignObject
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={node.height}
                  >
                    <div
                      className="figma-press-image"
                      onClick={() => props.handleImageClick(node)}
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "white"
                      }}
                    >
                      {node.imageUrl ? (
                        <img
                          src={node.imageUrl}
                          alt="Updated Image"
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <div>No Image Available</div>
                      )}
                    </div>
                  </foreignObject>
                )}
              </React.Fragment>
            );
          })}

          {/* map text nodes into overlays */}
          {/* keep text layers on top of image layers */}
          {props.data.text_nodes.map((node) => {
            const updatedNode = props.updatedTextNodes.find(n => n.id === node.id);

            if (props.isImageLoad && (node.is_updated || updatedNode)) {
              updateDesignImage(node);
            }

            loadFont({
              family: node.fontFamily,
              style: node.fontStyle,
              weight: node.fontWeight,
            });
            // create overlay elements on top of each text element of the image
            return (
              <React.Fragment key={node.id}>
                <rect
                  id={node.id}
                  data-text={node.id}
                  x={node.x}
                  y={node.y}
                  width={node.width}
                  height={node.height}
                  style={{
                    fill: "transparent"
                  }}
                  className="figma-press-text-node"
                  pointerEvents="all"
                  onClick={() => props.handleTextClick(node)}
                >
                  <title>{node.characters}</title>
                </rect>
                {props.isImageLoad && (node.is_updated || updatedNode) && (
                  <foreignObject
                    x={node.x}
                    y={node.y}
                    width={node.width}
                    height={Math.max(node.height, node.lineHeight)} // Use the maximum height
                  >
                    <div
                      className="figma-press-text"
                      style={{
                        fontFamily: node.fontFamily,
                        fontStyle: node.fontStyle,
                        fontSize: `${node.fontSize}px`,
                        fontWeight: node.fontWeight,
                        letterSpacing: node.letterSpacing > 3 ? 1 : node.letterSpacing,
                        lineHeight: `${node.lineHeight}%`,
                        textAlign: getTextAlignment(node.textAlignHorizontal),
                        color: textColors[node.id] || "black"
                      }}
                      onClick={() => props.handleTextClick(node)}
                    >
                      {node.characters}
                    </div>
                  </foreignObject>
                )}
              </React.Fragment>
            );
          })}
        </g>
      </svg>
    </>
  );
}
